import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Clock, Loader2, Gamepad2, ArrowRight, ShieldCheck, CheckSquare, Printer, Download } from "lucide-react";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import { absUrl } from "@/lib/seo";

export default function AcademyGuide() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setGuide(null); setErr(null);
    Promise.all([
      api.get(`/academy/guides/${slug}`),
      api.get(`/academy/checklists`).catch(() => ({ data: { items: [] } })),
    ])
      .then(([g, c]) => {
        setGuide(g.data);
        // Match by related_guide_slug
        const matched = (c.data.items || []).filter(
          (x) => x.related_guide_slug === slug
        );
        setChecklists(matched);
      })
      .catch((e) => setErr(formatApiError(e)));
  }, [slug]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-4 text-sm">{err}</div>
        <Link to="/academy" className="inline-block mt-4 text-sm text-emerald-700 hover:underline">← Back to Academy</Link>
      </div>
    );
  }
  if (!guide) {
    return <div className="max-w-3xl mx-auto px-4 py-16 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading guide…</div>;
  }

  const badge = audienceBadge(guide.audience);
  const backTo = guide.audience === "business" ? "/businesses/academy" : "/academy";
  const guideExcerpt = (guide.excerpt || guide.summary || guide.title || "").slice(0, 200);
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guideExcerpt,
    author: { "@type": "Organization", name: "ConfirmPH" },
    publisher: {
      "@type": "Organization",
      name: "ConfirmPH",
      logo: { "@type": "ImageObject", url: absUrl("/icon-512.png") },
    },
    inLanguage: "en-PH",
  };
  return (
    <div data-testid="academy-guide-page" className="bg-[#FAFAFA] min-h-[80vh]">
      <SEO
        title={`${guide.title}: ConfirmPH ${guide.audience === "business" ? "Business" : "Renter"} Guide`}
        description={guideExcerpt || `Read the ${guide.title} guide on ConfirmPH.`}
        canonical={`/academy/guide/${slug}`}
        og={{ type: "article" }}
        jsonLd={[articleJsonLd]}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Link to={backTo} data-testid="guide-back-link"
               className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700">
          <ArrowLeft className="w-4 h-4" /> Back to Academy
        </Link>

        <div className="mt-5 flex items-center gap-2 text-xs">
          <span className={`px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
          {guide.category && <span className="text-slate-500">· {guide.category}</span>}
          <span className="text-slate-500 inline-flex items-center gap-1 ml-auto">
            <Clock className="w-3.5 h-3.5" /> {guide.reading_time_minutes || 5} min read
          </span>
        </div>

        <h1 data-testid="guide-title"
             className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 leading-tight">
          {guide.title}
        </h1>
        {guide.subtitle && (
          <p className="text-slate-600 text-base sm:text-lg mt-3">{guide.subtitle}</p>
        )}

        {/* TOP CHECKLIST CTA */}
        {checklists.length > 0 && (
          <ChecklistCTA checklists={checklists} testIdSuffix="top" />
        )}

        <article data-testid="guide-body" className="prose prose-slate prose-emerald max-w-none mt-8">
          <ReactMarkdown
            components={{
              h2: ({ node, ...props }) => <h2 className="font-display text-2xl font-semibold mt-10 mb-3 text-slate-900" {...props} />,
              h3: ({ node, ...props }) => <h3 className="font-display text-xl font-semibold mt-7 mb-2 text-slate-900" {...props} />,
              h4: ({ node, ...props }) => <h4 className="font-display text-lg font-semibold mt-6 mb-2 text-slate-900" {...props} />,
              p:  ({ node, ...props }) => <p  className="text-slate-700 leading-relaxed my-3" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-1.5 my-3 text-slate-700" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-1.5 my-3 text-slate-700" {...props} />,
              li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-emerald-400 bg-emerald-50/60 pl-4 pr-3 py-3 my-5 rounded-r text-slate-800" {...props} />
              ),
              strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900" {...props} />,
              a: ({ node, ...props }) => <a className="text-emerald-700 hover:underline" {...props} />,
              code: ({ node, ...props }) => <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm" {...props} />,
            }}>
            {guide.body_markdown || ""}
          </ReactMarkdown>
        </article>

        {/* Native-style ad placement inside guide articles. Disabled
            unless ADS + WEB_ADS env are enabled and AdSense IDs exist. */}
        <AdSlot placement="guides_inline" className="mt-6" />

        {/* BOTTOM CHECKLIST CTA */}
        {checklists.length > 0 && (
          <ChecklistCTA checklists={checklists} testIdSuffix="bottom" />
        )}

        {/* RELATED QUIZZES */}
        {guide.related_quizzes && guide.related_quizzes.length > 0 && (
          <div className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5"
                data-testid="guide-related-quizzes">
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1">
              <Gamepad2 className="w-3.5 h-3.5" /> Test your knowledge
            </div>
            <div className="mt-3 space-y-2">
              {guide.related_quizzes.map((q) => (
                <Link key={q.id} to={`/academy/quiz/${q.slug}`}
                       data-testid={`guide-related-quiz-${q.slug}`}
                       className="flex items-center justify-between rounded-lg bg-white border border-emerald-200 p-3 hover:border-emerald-400">
                  <div>
                    <div className="font-medium text-slate-900">{q.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{q.description}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-emerald-700" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* TRUST STRIP */}
        <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-5 flex gap-3 items-start"
              data-testid="guide-trust-strip">
          <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
          <div className="text-sm text-slate-600">
            ConfirmPH is a booking confirmation layer for Philippine rentals, not a marketplace.
            The Academy is free and continually improved by our editorial team.
            Spotted an error? <Link to="/contact" className="text-emerald-700 hover:underline">Tell us</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}

function audienceBadge(a) {
  if (a === "renter")   return { label: "For renters",   cls: "bg-sky-50 text-sky-700" };
  if (a === "business") return { label: "For operators", cls: "bg-amber-50 text-amber-700" };
  return { label: "Everyone", cls: "bg-slate-100 text-slate-700" };
}

function ChecklistCTA({ checklists, testIdSuffix }) {
  return (
    <div data-testid={`guide-checklist-cta-${testIdSuffix}`}
         className="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white border border-emerald-200 grid place-items-center flex-shrink-0">
          <CheckSquare className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">Print-ready checklist</div>
          <div className="font-display text-lg font-semibold mt-0.5">Download the Renter Safety Checklist</div>
          <div className="text-sm text-slate-700 mt-1">
            A practical before / during / after checkbox sheet you can work through on your phone, print, or save as PDF.
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {checklists.map((c) => (
          <div key={c.id} className="contents">
            <Link to={`/academy/checklists/${c.slug}`}
                  data-testid={`guide-checklist-view-${testIdSuffix}-${c.slug}`}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-xs font-medium hover:bg-[#143027]">
              View checklist <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to={`/academy/checklists/${c.slug}?print=1`}
                  data-testid={`guide-checklist-print-${testIdSuffix}-${c.slug}`}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-800 text-xs font-medium hover:bg-slate-50">
              <Printer className="w-3.5 h-3.5" /> Print
            </Link>
            <Link to={`/academy/checklists/${c.slug}?print=1`}
                  data-testid={`guide-checklist-download-${testIdSuffix}-${c.slug}`}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-800 text-xs font-medium hover:bg-slate-50">
              <Download className="w-3.5 h-3.5" /> Download (PDF)
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

